import { getDocumentsWithToken } from "@services/azureDataApi";
import { SasTokenPrefix } from "@services/user/types";
import { useMutation } from "@tanstack/react-query";

const getExt = (u: string) => (u.split("?")[0].split("#")[0].split(".").pop() || "").toLowerCase();

const isPdf = (ext: string, mime: string) => ext === "pdf" || mime === "application/pdf";

const isImage = (ext: string, mime: string) =>
  mime.startsWith("image/") || ext === "jpg" || ext === "jpeg" || ext === "png";

const toViewableMime = (ext: string, mime: string) => {
  if (isPdf(ext, mime)) return "application/pdf";
  if (mime.startsWith("image/")) return mime;
  if (ext === "jpg" || ext === "jpeg") return "image/jpeg";
  if (ext === "png") return "image/png";
  return "application/octet-stream";
};

export const useDownloadPortServiceDocument = () => {
  const documentMutation = useMutation({
    mutationFn: async ({ url }: { service: string; url: string }) => {
      return getDocumentsWithToken(url, SasTokenPrefix.MOVEMENT_SERVICE);
    },
  });

  const handleDownload = async (service: string, documentUrl: string, filename?: string) => {
    try {
      if (!documentUrl) {
        console.error("Document URL is required");
        return;
      }

      const blob = await documentMutation.mutateAsync({ service, url: documentUrl });
      const defaultName = filename || "document.pdf";
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = defaultName;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Download failed:", err);
    }
  };

  const handlePreview = async (service: string, documentUrl: string) => {
    try {
      if (!documentUrl) {
        console.error("Document URL is required");
        return;
      }

      const blob = await documentMutation.mutateAsync({ service, url: documentUrl });
      const ext = getExt(documentUrl);
      const mime = blob.type || "";

      if (isPdf(ext, mime) || isImage(ext, mime)) {
        const viewMime = toViewableMime(ext, mime);
        const objUrl = URL.createObjectURL(new Blob([blob], { type: viewMime }));
        window.open(objUrl, "_blank", "noopener,noreferrer");
        return;
      }

      const objUrl = URL.createObjectURL(blob);
      window.open(objUrl, "_blank", "noopener,noreferrer");
    } catch (err) {
      console.error("Preview failed:", err);
    }
  };

  return {
    handleDownload,
    handlePreview,
    isPending: documentMutation.isPending,
    isError: documentMutation.isError,
    error: documentMutation.error,
  };
};
